/**
 * Identity Authentication Provider
 * React Context Provider for Identity-based authentication
 * Manages authentication state across the application
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { identityAuth, SessionStatus } from './IdentityAuth';
import { getIdentityUrl as identityUrlForCurrentHost } from '../utils/Config';
import { setAuthProviderInstance } from './errorHandler';
import paths from '../routes/paths';

// Create authentication context
const IdentityAuthContext = createContext();

// The provider sits above the router, so it cannot tell a protected page from an anonymous one
// (pupil, QR and survey-view pages are not in the public list below). Bounding the retries gives
// a protected page time to ride out an Identity blip without leaving anonymous pages polling an
// unreachable Identity for as long as they stay open.
const MAX_UNVERIFIED_SESSION_RETRIES = 3;

// Custom hook to use authentication context
export const useIdentityAuth = () => {
  const context = useContext(IdentityAuthContext);
  if (!context) {
    throw new Error('useIdentityAuth must be used within an IdentityAuthProvider');
  }
  return context;
};

// Authentication Provider Component
export const IdentityAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  const isPublicRoute = (pathname) => {
    const publicRoutePatterns = [
      '/replies/',           // participantsReply is Example of a public route that does not require authentication
      '/kiosk',              // Kiosk mode - public, no authentication required
    ];

    return publicRoutePatterns.some(pattern => {
   
      return pathname === pattern || pathname.startsWith(pattern);
    });
  };

  useEffect(() => {
    let retryTimeout = null;
    let unverifiedRetries = 0;
    let cancelled = false;

    const checkAuth = async () => {
      let retryScheduled = false;
      try {
        setLoading(true);
        
        const currentPath = window.location.pathname;
        
        if (isPublicRoute(currentPath)) {
          setLoading(false);
          setAuthenticated(false);
          setUser(null);
          return;
        }
        
        // Use IdentityAuth to check authentication for protected routes
        const status = await identityAuth.checkSession();
        if (cancelled) return;

        // Identity being briefly unreachable is not a sign-out: stay on the loading screen and
        // ask again rather than letting the route guards bounce the user to the login page.
        if (
          status === SessionStatus.Unverified &&
          unverifiedRetries < MAX_UNVERIFIED_SESSION_RETRIES
        ) {
          unverifiedRetries += 1;
          retryScheduled = true;
          retryTimeout = setTimeout(checkAuth, identityAuth.retryDelayMs());
          return;
        }

        const isAuth = status === SessionStatus.Authenticated;
        setAuthenticated(isAuth);

        if (isAuth) {
          const userInfo = identityAuth.getUserInfo();
          
          if (userInfo) {
            setUser({
              // Basic user info
              userId: userInfo.userId,
              email: userInfo.email,
              firstName: userInfo.name ? userInfo.name.split(' ')[0] : '',
              lastName: userInfo.name ? userInfo.name.split(' ').slice(1).join(' ') : '',
              name: userInfo.name,
              roles: userInfo.roles || [],
              userType: userInfo.userType,
              
              // Organization info
              groups: userInfo.groups || [],
              schools: userInfo.schools || [],
              isMat: userInfo.isMat || false,
              matGroupId: userInfo.matGroupId,
              schoolId: userInfo.schoolId,
              allSchools: userInfo.allSchools || false,
              
              // Access control info
              accessControl: userInfo.accessControl || {},
              
              // Token metadata
              claimsVersion: userInfo.claimsVersion,
              issuedAt: userInfo.issuedAt,
              expiresAt: userInfo.expiresAt
            });
            
            // Start periodic session checks
            identityAuth.startSessionCheck();
          } else {
            setAuthenticated(false);
            setUser(null);
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        setAuthenticated(false);
        setUser(null);
      } finally {
        if (!retryScheduled) {
          setLoading(false);
        }
      }
    };

    checkAuth();
    
    // Register this provider instance with the error handler
    setAuthProviderInstance({ handleSessionExpired });
    
    // Cleanup on unmount
    return () => {
      cancelled = true;
      clearTimeout(retryTimeout);
      identityAuth.stopSessionCheck();
      setAuthProviderInstance(null);
    };
  }, []); // Only run once on mount - route changes handled by ProtectedRoute/PublicRoute

  const login = async (loginResponse) => {
    try {
      // IdentityAuth handles login via Identity app
      // This method is mainly for compatibility
      const isAuth = await identityAuth.checkAuth();
      setAuthenticated(isAuth);
      
      if (isAuth) {
        const userInfo = identityAuth.getUserInfo();
        if (userInfo) {
          setUser({
            userId: userInfo.userId,
            email: userInfo.email,
            firstName: userInfo.name ? userInfo.name.split(' ')[0] : '',
            lastName: userInfo.name ? userInfo.name.split(' ').slice(1).join(' ') : '',
            name: userInfo.name,
            roles: userInfo.roles || [],
            userType: userInfo.userType,
            // Organization info
            groups: userInfo.groups || [],
            schools: userInfo.schools || [],
            isMat: userInfo.isMat || false,
            matGroupId: userInfo.matGroupId,
            schoolId: userInfo.schoolId,
            
            // Access control info
            accessControl: userInfo.accessControl || {},
            
            // Token metadata
            claimsVersion: userInfo.claimsVersion,
            issuedAt: userInfo.issuedAt,
            expiresAt: userInfo.expiresAt
          });
        }
      }
      
      return isAuth;
    } catch (error) {
      return false;
    }
  };

  const logout = async () => {
    try {
      await identityAuth.logout();
      setAuthenticated(false);
      setUser(null);
    } catch (error) {
      setAuthenticated(false);
      setUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      const isAuth = await identityAuth.checkAuth();
      setAuthenticated(isAuth);
      
      if (isAuth) {
        const userInfo = identityAuth.getUserInfo();
        if (userInfo) {
          setUser({
            userId: userInfo.userId,
            email: userInfo.email,
            firstName: userInfo.name ? userInfo.name.split(' ')[0] : '',
            lastName: userInfo.name ? userInfo.name.split(' ').slice(1).join(' ') : '',
            name: userInfo.name,
            roles: userInfo.roles || [],
            userType: userInfo.userType,
            // Organization info
            groups: userInfo.groups || [],
            schools: userInfo.schools || [],
            isMat: userInfo.isMat || false,
            matGroupId: userInfo.matGroupId,
            schoolId: userInfo.schoolId,
            
            // Access control info
            accessControl: userInfo.accessControl || {},
            
            // Token metadata
            claimsVersion: userInfo.claimsVersion,
            issuedAt: userInfo.issuedAt,
            expiresAt: userInfo.expiresAt
          });
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      setAuthenticated(false);
      setUser(null);
    }
  };

  const hasRole = (role) => {
    return identityAuth.hasRole(role);
  };

  const getToken = () => {
    return identityAuth.getToken();
  };

  const fetchWithAuth = (url, options) => {
    return identityAuth.fetchWithAuth(url, options);
  };

  const handleSessionExpired = () => {
    setAuthenticated(false);
    setUser(null);
    
    // Redirect to Identity App for authentication
    const identityUrl = identityUrlForCurrentHost();
    window.location.href = `${identityUrl}/login`;
  };

  const checkForSessionExpired = (error) => {
    // Check if error indicates session expired
    const isSessionExpired = error && (
      // Check response data
      (error.response && error.response.data && (
        (typeof error.response.data === 'string' && error.response.data.includes('Session expired')) ||
        (typeof error.response.data === 'object' && error.response.data.error && error.response.data.error.includes('Session expired'))
      )) ||
      // Check status codes
      (error.response && (error.response.status === 401 || error.response.status === 403)) ||
      // Check error message
      (error.message && error.message.includes('Session expired'))
    );

    if (isSessionExpired) {
      handleSessionExpired();
      return true;
    }
    
    return false;
  };

  const value = {
    user,
    loading,
    authenticated,
    login,
    logout,
    refreshUser,
    hasRole,
    getToken,
    fetchWithAuth,
    handleSessionExpired,
    checkForSessionExpired,
  };

  return (
    <IdentityAuthContext.Provider value={value}>
      {children}
    </IdentityAuthContext.Provider>
  );
};
